import React, { FC } from 'react';

import Input from 'components/Input/Input';
import RichText from 'components/RichText/RichText';
import Switch from 'components/Switch/Switch';
import { FormControl, FormField, FormItem, FormMessage } from 'components/ui';
import { ColorPicker } from 'components/ui/ColorPicker/color-picker';
import UploadImage from 'components/UploadImage/UploadImage';

import Textarea from '../../components/Textarea/Textarea';

import { DesignLayoutSettingsProps } from './types';

const DesignLayoutSettings: FC<DesignLayoutSettingsProps> = ({
  control,
  errors,
  hasFeatureFlagShowLoginButton
}) => {
  return (
    <>
      <div className="border border-base-sidebarBorder rounded-[20px] mb-5 p-9">
        <RichText
          title="General Settings"
          description="Manage your portal’s identity and appearance"
          className="mb-9"
        />
        <div className="grid grid-cols-2 gap-9 w-[586px]">
          <FormField
            name="generalBackgroundColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="General Background Color"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="mt-9 w-[586px]">
          <FormField
            name="generalFavicon"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <UploadImage
                    className="w-full"
                    title="Add a favicon"
                    description={`Add a favicon to your page`}
                    buttonText="Upload"
                    selectedImageUrl={value}
                    onUpload={(url: string) => {
                      onChange(url);
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>

      <div className="border border-base-sidebarBorder rounded-[20px] mb-5 p-9">
        <RichText
          title="Header"
          description="Customize your portal’s header"
          className="mb-9 [&>h4]:text-p-l [&>h4]:font-semibold"
        />
        <div className="flex flex-col w-[586px]">
          <div className="mb-9">
            <FormField
              name="headerLogo"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <UploadImage
                      className="w-full"
                      title="Add a logo"
                      description={`Add an icon to your page.\nImage will be scaled to 130px x 60px`}
                      buttonText="Upload"
                      selectedImageUrl={value}
                      onUpload={(url: string) => {
                        onChange(url);
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-2 mb-9 gap-9">
            <FormField
              name="headerBackgroundColor"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <ColorPicker
                      label="Background Color"
                      onChange={onChange}
                      defaultValue={value}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              name="headerTextColor"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <ColorPicker
                      label="Text Color"
                      onChange={onChange}
                      defaultValue={value}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-p-s text-base-destructive" />
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-1 gap-y-9">
            <FormField
              name="headerIsPlayerNameShown"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Switch
                      label="Show player name"
                      description="Display the logged-in player's name in the header"
                      textAlignment="right"
                      defaultChecked={value}
                      size="lg"
                      onCheckedChange={onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              name="headerIsPlayerBalanceShown"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Switch
                      label="Show player balance"
                      description="Display the logged-in player's account balance in the header"
                      textAlignment="right"
                      defaultChecked={value}
                      size="lg"
                      onCheckedChange={onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {hasFeatureFlagShowLoginButton && (
              <FormField
                name="headerIsLoginBtnShown"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <FormItem>
                    <FormControl>
                      <Switch
                        label="Show login button"
                        description="Display a login button in the header"
                        textAlignment="right"
                        defaultChecked={value}
                        size="lg"
                        onCheckedChange={onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
            <FormField
              name="headerIsLanguageSwitcherShown"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormItem>
                  <FormControl>
                    <Switch
                      label="Show language switcher"
                      description="Display a dropdown menu for allowing players to change the portal's language"
                      textAlignment="right"
                      defaultChecked={value}
                      size="lg"
                      onCheckedChange={onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
      <div className="border border-base-sidebarBorder rounded-[20px] mb-5 p-9">
        <RichText
          title="SEO Settings"
          description="Customize how this page appears in search results and social media previews."
          className="mb-9"
        />
        <div className="grid grid-cols-1 gap-y-9 w-[586px]">
          <FormField
            name="seoTitle"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="SEO Title"
                    placeholder="Add a title "
                    value={value}
                    onChange={onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="seoDescription"
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    label="Description"
                    placeholder="Write a short summary that describes this page for search engines.."
                    {...field}
                  />
                </FormControl>
                <FormMessage className="mt-1 text-p-s text-base-destructive" />
              </FormItem>
            )}
          />
          <FormField
            name="seoKeywords"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="Keywords"
                    placeholder="Enter keywords, separated by commas"
                    onChange={(e) =>
                      onChange(e.target.value.split(',').map((v) => v.trim()))
                    }
                    value={value && value?.length > 0 ? value.join(', ') : ''}
                  />
                </FormControl>
                <FormMessage className="mt-1 text-p-s text-base-destructive" />
              </FormItem>
            )}
          />
          <FormField
            name="seoImg"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <UploadImage
                    className="w-full"
                    title="SEO / Social Share Image"
                    description={`JPG, PNG, WebP — recommended size 1200x630px`}
                    buttonText="Upload"
                    selectedImageUrl={value}
                    onUpload={(url: string) => {
                      onChange(url);
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
      <div className="border border-base-sidebarBorder rounded-[20px] mb-5 p-9">
        <RichText
          title="Sidebar"
          description="Customize your portal’s sidebar"
          className="mb-9"
        />
        <div className="grid grid-cols-2 w-[586px] xl:grid-cols-3 gap-9 xl:w-[897px]">
          <FormField
            name="sidebarBackgroundColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Background Color"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="sidebarTextColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Text Color"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="sidebarHoverColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Tab on-hover color"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
      <div className="border border-base-sidebarBorder rounded-[20px] mb-7 p-9">
        <RichText
          title="Footer"
          description="Customize your portal’s footer"
          className="mb-9"
        />
        <div className="grid grid-cols-2 mb-9 gap-9 w-[586px]">
          <FormField
            name="footerBackgroundColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Background Color"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="footerTextColor"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <ColorPicker
                    label="Text Color"
                    onChange={onChange}
                    defaultValue={value}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-1 gap-9 w-[586px]">
          <FormField
            name="footerPrivacyLink"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="Privacy link"
                    placeholder="htpp://"
                    value={value}
                    onChange={onChange}
                    state={
                      errors.footerPrivacyLink?.message ? 'error' : 'default'
                    }
                    description={errors.footerPrivacyLink?.message}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="footerTermsConditionsLink"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="Terms & Conditions link"
                    placeholder="htpp://"
                    value={value}
                    onChange={onChange}
                    state={
                      errors.footerTermsConditionsLink?.message
                        ? 'error'
                        : 'default'
                    }
                    description={errors.footerTermsConditionsLink?.message}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="footerSupportLink"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem>
                <FormControl>
                  <Input
                    label="Support link"
                    placeholder="htpp://"
                    value={value}
                    onChange={onChange}
                    state={
                      errors.footerSupportLink?.message ? 'error' : 'default'
                    }
                    description={errors.footerSupportLink?.message}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
    </>
  );
};

export default DesignLayoutSettings;
